import type { SnapshotCommand } from '../../services/scmMockApi';
import example from './scenario-example.json';

export type ScenarioEntry = {
    resource: 'transport-requests' | 'transports';
    id: string;
    command: SnapshotCommand;
};
export type DemoScenario = { name: string; entries: ScenarioEntry[] };
const object = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);
const uuid = (value: unknown): value is string =>
    typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export function newExampleScenario(): string {
    const ids = new Map<string, string>();
    const fresh = JSON.parse(JSON.stringify(example), (_key, value: unknown) => {
        if (!uuid(value)) {
            return value;
        }
        if (!ids.has(value)) {
            ids.set(value, crypto.randomUUID());
        }
        return ids.get(value);
    });
    return JSON.stringify(fresh, null, 4);
}

// Only fixed, existing mock endpoints are reachable. URLs, headers and tokens are never imported.
export function parseDemoScenario(text: string): DemoScenario {
    if (new TextEncoder().encode(text).length > 2_000_000) {
        throw new Error('JSON must be smaller than 2 MB.');
    }
    const value: unknown = JSON.parse(text);
    if (!object(value) || typeof value.name !== 'string' || !value.name.trim() || value.name.length > 200) {
        throw new Error('Provide a scenario name (1–200 characters).');
    }
    if (Object.keys(value).some(key => !['name', 'requests', 'transports'].includes(key))) {
        throw new Error('Only name, requests and transports are supported.');
    }
    const entries: ScenarioEntry[] = [];
    for (const [key, resource] of [
        ['requests', 'transport-requests'],
        ['transports', 'transports'],
    ] as const) {
        const records = value[key] === undefined ? [] : value[key];
        if (!Array.isArray(records)) {
            throw new Error(`${key} must be an array.`);
        }
        const ids = new Set<string>();
        for (const [index, record] of records.entries()) {
            const path = `${key}[${index}]`;
            if (!object(record) || !uuid(record.id) || !object(record.snapshot)) {
                throw new Error(`${path} needs a UUID id and a snapshot object.`);
            }
            if (Object.keys(record).some(field => !['id', 'snapshot', 'revision', 'occurred_at'].includes(field))) {
                throw new Error(`${path} supports only id, snapshot, revision and occurred_at.`);
            }
            const id = record.id.toLowerCase();
            if (ids.has(id)) {
                throw new Error(`${path} repeats an ID in this batch. Import revisions separately.`);
            }
            ids.add(id);
            if (resource === 'transports' && record.snapshot.id !== id) {
                throw new Error(`${path}.snapshot.id must match id (lowercase UUID).`);
            }
            if (
                record.revision !== undefined &&
                (!Number.isSafeInteger(record.revision) || Number(record.revision) < 1)
            ) {
                throw new Error(`${path}.revision must be a positive safe integer.`);
            }
            if (
                record.occurred_at !== undefined &&
                (typeof record.occurred_at !== 'string' ||
                    !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(record.occurred_at) ||
                    !Number.isFinite(Date.parse(record.occurred_at)))
            ) {
                throw new Error(`${path}.occurred_at must be an ISO timestamp with a timezone.`);
            }
            entries.push({
                resource,
                id,
                command: {
                    snapshot: record.snapshot,
                    ...(record.revision === undefined ? {} : { revision: record.revision as number }),
                    ...(record.occurred_at === undefined ? {} : { occurred_at: record.occurred_at as string }),
                },
            });
        }
    }
    if (entries.length < 1 || entries.length > 100) {
        throw new Error('Provide 1–100 records per scenario.');
    }
    return { name: value.name.trim(), entries };
}
